import { useEffect, useRef, useState } from "react";

import { toast } from "react-hot-toast";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";

export const ReadonlyEmail = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 5px;

  cursor: default;

  display: flex;
  justify-content: center;

  overflow-x: auto;
  max-width: 100%;
  min-width: 0;

  iframe {
    overflow: hidden;
    border: none;
    width: 100%;
    max-width: 100%;
    min-width: min(650px, 100%);

    border: none;
    display: block;
  }
`;

type EmailPreviewProps = {
  isOpen: boolean;
  onClose: () => void;
  forType: string;
  forId: string;
  announcement: any;
};

const EmailPreviewModal = ({
  announcement,
  isOpen,
  onClose,
  forType,
  forId,
}: EmailPreviewProps) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLoading(true);
    api.announcements.getEmailPreview(
      forType,
      forId,
      announcement,
      ({ html }) => {
        setLoading(false);
        setPreviewHtml(html);
      },
      (err) => {
        toast.error(err.message);
        onClose();
        setLoading(false);
      },
    );
  }, [forType, forId, onClose, announcement, isOpen]);

  // Make links clickable and open in new tabs
  useEffect(() => {
    if (!frameRef.current || !previewHtml) {
      return;
    }

    const iframe = frameRef.current;
    const handleLoad = () => {
      try {
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          return;
        }

        // Find all links and make them open in new tabs
        const links = iframeDoc.querySelectorAll("a");
        links.forEach((link) => {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        });
      } catch (error) {
        // Cross-origin restrictions may prevent access
        console.warn("Could not modify iframe links:", error);
      }
    };

    iframe.addEventListener("load", handleLoad);
    // Also try immediately in case iframe is already loaded
    handleLoad();

    return () => {
      iframe.removeEventListener("load", handleLoad);
    };
  }, [previewHtml]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalHeader>Email Preview</ModalHeader>
      <ModalContent scrolling>
        {loading && <Loader>Loading email preview...</Loader>}
        <ReadonlyEmail>
          <iframe
            scrolling="no"
            ref={frameRef}
            onLoad={() => {
              if (!frameRef.current || !frameRef.current.contentWindow) {
                return;
              }
              frameRef.current.style.height = `${frameRef.current.contentWindow.document.body.scrollHeight}px`;
            }}
            srcDoc={previewHtml.replace(
              'src="cid:bannerImage"',
              'src="cid:bannerImage" height="150"',
            )}
            title="Email content"
          />
        </ReadonlyEmail>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Close</Button>
      </ModalActions>
    </Modal>
  );
};
export default EmailPreviewModal;
